import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { WhatsApp } from 'constants/messageMediums';
import {
  WhatsAppButtonDefinition,
  WhatsAppContentDefinition,
  WhatsAppHeaderDefinition,
} from 'modules/graphql';
import {
  getContentWithData,
  getPreviewWithDataError,
  getTemplateHasContent,
  PreviewWithDataErrorMessage,
} from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { usePreviewWithDataContext } from 'modules/templateEditor/context/PreviewWithDataContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useGetTemplateUserJsonTestData } from 'modules/templateEditor/hooks/useGetTemplateUserJsonTestData';

import { WhatsAppPreviewContext } from './WhatsAppPreviewContext';

export const WhatsAppPreviewContextProvider: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const { liveTemplate } = useEditableFieldsContext();
  const whatsAppData = useMemo(
    () => (liveTemplate?.mediumData || {}) as WhatsAppContentDefinition,
    [liveTemplate],
  );
  const { canRefreshPreview, dataFeedData, userData, isUserDataDirty, setIsUserDataDirty } =
    usePreviewWithDataContext();
  const { templateId } = useQueryParamContext();

  const hasLoadedData =
    (!!userData && userData !== '{}') || (!!dataFeedData && dataFeedData !== '{}');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [header, setHeader] = useState<WhatsAppHeaderDefinition>({});
  const [message, setMessage] = useState<string>('');
  const [buttons, setButtons] = useState<WhatsAppButtonDefinition[]>([]);
  const [previewError, setPreviewError] = useState<PreviewWithDataErrorMessage | undefined>(
    undefined,
  );

  const hasContent = getTemplateHasContent(WhatsApp, whatsAppData);
  const locale = whatsAppData?.locale?.name || '';

  const { getTemplateUserJsonTestData } = useGetTemplateUserJsonTestData();

  const onRefreshPreview = useCallback(async () => {
    // Only make request when template data has been received
    if (canRefreshPreview && hasContent) {
      setIsLoading(true);

      const userDataToSave = await getTemplateUserJsonTestData(userData, isUserDataDirty);

      getContentWithData(
        templateId,
        {
          subject: '',
          whatsAppHeader: whatsAppData.header && {
            // @ts-ignore
            [whatsAppData.header.__typename]: whatsAppData.header,
          },
          whatsAppMessage: whatsAppData.message,
          // @ts-ignore
          whatsAppButtons: whatsAppData.buttons?.map((button) => ({ [button.__typename]: button })),
        },
        JSON.parse(dataFeedData),
        JSON.parse(userDataToSave),
      )
        .then(({ data }) => {
          setIsUserDataDirty(false);
          setHeader(data.whatsAppHeader && Object.values(data.whatsAppHeader)[0]);
          setMessage(data.whatsAppMessage);
          setButtons(
            data.whatsAppButtons.map(
              (button: { [buttonType: string]: WhatsAppButtonDefinition }) =>
                Object.values(button)[0],
            ),
          );
          setPreviewError(undefined);
        })
        .catch(({ response }) => {
          setPreviewError(getPreviewWithDataError(response?.data?.message || ''));
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [
    canRefreshPreview,
    dataFeedData,
    templateId,
    userData,
    hasContent,
    whatsAppData,
    isUserDataDirty,
    setIsUserDataDirty,
    getTemplateUserJsonTestData,
  ]);

  useEffect(() => {
    if (canRefreshPreview) {
      onRefreshPreview();
    }
  }, [canRefreshPreview, dataFeedData, userData, locale]);

  return (
    <WhatsAppPreviewContext.Provider
      value={{
        whatsAppHeader: header,
        whatsAppMessage: message,
        whatsAppButtons: buttons,
        isLoading,
        hasContent,
        hasLoadedData,
        previewError,
        onRefreshPreview,
      }}
    >
      {children}
    </WhatsAppPreviewContext.Provider>
  );
};
